import { isNightOffMeal } from './nightOff.js'

export const PLAN_DAY_KEYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

/** Original dinner plus two others, then the original returns. */
export const DINNERS_PER_NIGHT = 3

/**
 * One ring per cook night. Originals are assigned first so two nights never
 * start on the same recipe. Alternatives come from the rest of the list.
 * @param {object[]} recipes
 * @param {number} nightCount
 * @returns {object[][]}
 */
export function buildDinnerRings(recipes, nightCount) {
  const pool = []
  const seen = new Set()
  for (const recipe of recipes || []) {
    const recipeId = recipe?.recipeId ? String(recipe.recipeId) : ''
    const name = typeof recipe?.name === 'string' ? recipe.name.trim() : ''
    if (!recipeId || !name || seen.has(recipeId)) continue
    seen.add(recipeId)
    pool.push(recipe)
  }
  const count = Math.max(0, Math.min(Number(nightCount) || 0, pool.length))
  const originals = pool.slice(0, count)
  const originalIds = new Set(originals.map((recipe) => String(recipe.recipeId)))
  const alts = pool.filter((recipe) => !originalIds.has(String(recipe.recipeId)))
  return originals.map((original, index) => {
    const ring = [original]
    if (!alts.length) return ring
    const start = index % alts.length
    let cursor = 0
    while (ring.length < DINNERS_PER_NIGHT) {
      const candidate = alts[(start + cursor) % alts.length]
      cursor += 1
      if (ring.some((recipe) => String(recipe.recipeId) === String(candidate.recipeId))) break
      ring.push(candidate)
    }
    return ring
  })
}

/** @param {object[]} ring @param {number} offset */
export function dinnerAt(ring, offset) {
  if (!ring?.length) return null
  const index = ((Number(offset) || 0) % ring.length + ring.length) % ring.length
  return ring[index]
}

const DAY_INDEX = Object.fromEntries(PLAN_DAY_KEYS.map((key, index) => [key, index]))
const DAY_NAME = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

export function dayShortLabel(dayKey) {
  const index = DAY_INDEX[dayKey]
  return index == null ? dayKey : DAY_SHORT[index]
}

export function openPlanDayKeys(assignedByDay, stripDays = []) {
  const past = new Set((stripDays || []).filter((day) => day?.isPast).map((day) => day.dayKey))
  return PLAN_DAY_KEYS.filter((key) => {
    if (past.has(key)) return false
    const meal = assignedByDay?.[key]
    if (isNightOffMeal(meal)) return false
    if (meal?.recipeId) return false
    return true
  })
}

export function weekHasPastNight(stripDays = []) {
  return (stripDays || []).some((day) => day?.isPast)
}

function clampCookNights(value) {
  const parsed = Number(value)
  if (!Number.isFinite(parsed)) return 3
  return Math.max(1, Math.min(6, Math.round(parsed)))
}

function hasSavedStructure(structure) {
  return Boolean(structure && typeof structure === 'object' && Object.keys(structure).length > 0)
}

export function seedDraft(structure, { weekFull = false, openDayKeys = [], weekHasPast = false } = {}) {
  const saved = hasSavedStructure(structure)
  const preferredCookNights = saved ? clampCookNights(structure.default_cook_nights) : 3
  const availableCookNights = weekFull
    ? 6
    : Math.max(1, Math.min(6, openDayKeys.length))
  const easy = Array.isArray(structure?.easy_night_types) ? structure.easy_night_types : []
  const eatOut = (Array.isArray(structure?.eat_out_days) ? structure.eat_out_days : [])
    .map((day) => String(day).trim().toLowerCase())
  let eatOutDay = null
  if (saved) {
    if (eatOut.includes('sat')) eatOutDay = 'sat'
    else if (eatOut.includes('fri')) eatOutDay = 'fri'
  }
  return {
    scope: weekFull ? 'next_week' : 'rest_of_week',
    days: [...openDayKeys],
    cookNights: Math.min(preferredCookNights, availableCookNights),
    leftovers: saved ? easy.includes('leftovers') : true,
    frozen: saved ? easy.includes('frozen') : false,
    eatOutDay,
    bias: 'mix',
    replaceExisting: false,
    hasUsualWeek: saved,
    weekHasPast: Boolean(weekHasPast),
    weekFull: Boolean(weekFull),
  }
}

export function cookNightCounts(draft, openDayKeys = []) {
  const scope = draft?.scope
  const available = scope === 'next_week'
    ? 6
    : scope === 'pick_nights'
      ? (draft?.days || []).length
      : (openDayKeys || []).length
  const minimum = scope === 'next_week' ? 3 : 1
  const maximum = Math.max(minimum, Math.min(6, available))
  return Array.from({ length: maximum - minimum + 1 }, (_, index) => minimum + index)
}

export function normalizeCookNights(draft, openDayKeys = []) {
  const counts = cookNightCounts(draft, openDayKeys)
  const requested = clampCookNights(draft?.cookNights)
  return Math.max(counts[0], Math.min(counts[counts.length - 1], requested))
}

function nightsForDraft(draft, openDayKeys) {
  if (draft?.scope === 'next_week') return [0, 1, 2, 3, 4, 5, 6]
  if (draft?.scope === 'pick_nights') {
    return (draft.days || [])
      .map((key) => DAY_INDEX[key])
      .filter((index) => index != null)
      .sort((a, b) => a - b)
  }
  return (openDayKeys || [])
    .map((key) => DAY_INDEX[key])
    .filter((index) => index != null)
}

function assignLight(fillableDays, {
  cookCount,
  eatOutIndexes,
  easyTypes,
  includeEatOut,
  fillRemainingEasy,
}) {
  const days = [...new Set(fillableDays.map(Number))].sort((a, b) => a - b)
  if (!days.length) return [[], {}]
  const cookTarget = Math.max(0, Math.min(cookCount, days.length))
  const nightOff = {}
  let remaining = [...days]
  if (includeEatOut && days.length > cookTarget) {
    const eatDay = (eatOutIndexes || []).find((candidate) => remaining.includes(candidate))
    if (eatDay != null) {
      nightOff[eatDay] = 'out'
      remaining = remaining.filter((day) => day !== eatDay)
    }
  }
  const cookDays = []
  for (const day of [0, 1, 2, 3, 6, 4, 5]) {
    if (remaining.includes(day) && cookDays.length < cookTarget) cookDays.push(day)
  }
  for (const day of remaining) {
    if (cookDays.length < cookTarget && !cookDays.includes(day)) cookDays.push(day)
  }
  const cookSet = new Set(cookDays)
  if (!fillRemainingEasy) return [cookDays.sort((a, b) => a - b), nightOff]
  const cycle = (easyTypes || []).filter((type) => type && type !== 'out')
  const types = cycle.length ? cycle : ['leftovers', 'frozen']
  remaining
    .filter((day) => !cookSet.has(day))
    .sort((a, b) => a - b)
    .forEach((day, index) => {
      nightOff[day] = types[index % types.length]
    })
  return [cookDays.sort((a, b) => a - b), nightOff]
}

export function allocatePreview(draft, openDayKeys = []) {
  const fillable = nightsForDraft(draft, openDayKeys)
  const easy = []
  if (draft?.leftovers) easy.push('leftovers')
  if (draft?.frozen) easy.push('frozen')
  const eatOutIndexes = draft?.eatOutDay === 'fri' ? [4] : draft?.eatOutDay === 'sat' ? [5] : []
  return assignLight(fillable, {
    cookCount: Math.min(clampCookNights(draft?.cookNights), fillable.length),
    eatOutIndexes,
    easyTypes: easy.length ? easy : ['leftovers'],
    includeEatOut: eatOutIndexes.length > 0,
    fillRemainingEasy: easy.length > 0,
  })
}

function joinNames(indexes) {
  const names = [...indexes].sort((a, b) => a - b).map((index) => DAY_NAME[index])
  if (names.length <= 1) return names[0] || ''
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

function joinPhrases(parts) {
  if (parts.length <= 1) return parts[0] || ''
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`
}

export function previewSentence(draft, openDayKeys = []) {
  if (draft?.scope === 'next_week') {
    const extras = []
    if (draft.leftovers) extras.push('leftovers')
    if (draft.frozen) extras.push('heat and eat')
    if (draft.eatOutDay === 'fri') extras.push('eat out Friday')
    if (draft.eatOutDay === 'sat') extras.push('eat out Saturday')
    const nights = `${clampCookNights(draft.cookNights)} cook nights next week`
    if (!extras.length) return `${nights}. The other nights stay open.`
    return `${nights}, with ${joinPhrases(extras)}.`
  }
  const selected = nightsForDraft(draft, openDayKeys)
  if (!selected.length) return 'Pick at least one night.'
  const [cook, nightOff] = allocatePreview(draft, openDayKeys)
  const bits = [cook.length ? `${joinNames(cook)} to cook` : 'No cook nights']
  const grouped = { leftovers: [], frozen: [], out: [] }
  Object.entries(nightOff).forEach(([day, type]) => {
    if (grouped[type]) grouped[type].push(Number(day))
  })
  if (grouped.leftovers.length) bits.push(`Leftovers on ${joinNames(grouped.leftovers)}`)
  if (grouped.frozen.length) bits.push(`Heat and eat on ${joinNames(grouped.frozen)}`)
  if (grouped.out.length) bits.push(`Eat out ${joinNames(grouped.out)}`)
  const sentence = bits.map((bit, index) => (
    index === 0 ? bit.charAt(0).toUpperCase() + bit.slice(1) : bit
  ))
  return `${sentence.join('. ')}.`
}

export function receiptLine(draft, openDayKeys = []) {
  if (draft?.scope === 'next_week') {
    return `Planned ${clampCookNights(draft.cookNights)} dinners for next week.`
  }
  const selected = nightsForDraft(draft, openDayKeys)
  const [cook, nightOff] = allocatePreview(draft, openDayKeys)
  const easy = Object.keys(nightOff).length
  let untouched = 0
  if (draft?.scope === 'pick_nights') {
    const picked = new Set(draft.days || [])
    untouched = (openDayKeys || []).filter((key) => !picked.has(key)).length
  }
  const openLeft = Math.max(0, selected.length - cook.length - easy) + untouched
  if (openLeft === 0 && easy > 0) {
    return `Planned ${cook.length} dinners · rest are easy nights.`
  }
  const easyLabel = easy === 1 ? '1 easy night' : `${easy} easy nights`
  return `Planned ${cook.length} dinners · ${easyLabel} · ${openLeft} open.`
}

export function cookNightWarning(draft, openDayKeys = []) {
  const available = draft?.scope === 'next_week'
    ? 7
    : draft?.scope === 'pick_nights'
      ? (draft.days || []).length
      : (openDayKeys || []).length
  if (clampCookNights(draft?.cookNights) > available) {
    return 'That\u2019s more cook nights than open nights.'
  }
  return ''
}

export function canSubmitPlan(draft, openDayKeys = []) {
  if (draft?.scope === 'next_week') return true
  if (draft?.scope === 'pick_nights') return (draft?.days || []).length > 0
  return (openDayKeys || []).length > 0
}

/**
 * Dinners and nights off to write onto the week the person is looking at.
 * Card order follows cook-night order, so the first dinner lands on the first cook night.
 */
export function planWithCookPlacements(draft, openDayKeys = []) {
  const [cookIndexes, nightOff] = allocatePreview(draft, openDayKeys)
  const recipeIds = (Array.isArray(draft?.chosenRecipeIds) ? draft.chosenRecipeIds : [])
    .map((id) => String(id || '').trim())
    .filter(Boolean)
  const dinners = cookIndexes
    .map((index, position) => ({
      dayKey: PLAN_DAY_KEYS[index],
      recipeId: recipeIds[position] || '',
    }))
    .filter((row) => row.dayKey && row.recipeId)
  const nightsOff = Object.entries(nightOff || {})
    .map(([index, type]) => ({
      dayKey: PLAN_DAY_KEYS[Number(index)],
      nightOffType: type,
    }))
    .filter((row) => row.dayKey && row.nightOffType)
  return { dinners, nightsOff }
}

export function scopeChoices(draft, openDayKeys = []) {
  const openCount = (openDayKeys || []).length
  const choices = []
  if (openCount > 0) {
    choices.push({
      id: 'rest_of_week',
      label: draft?.weekHasPast ? 'Rest of week' : 'This week',
    })
  }
  choices.push({ id: 'next_week', label: 'Next week' })
  if (openCount > 0) {
    choices.push({ id: 'pick_nights', label: 'Pick nights' })
  }
  return choices
}

export function toPlanWithCookRequest(draft) {
  const easy = []
  if (draft?.leftovers) easy.push('leftovers')
  if (draft?.frozen) easy.push('frozen')
  const scope = draft?.scope === 'pick_nights' || draft?.scope === 'next_week'
    ? draft.scope
    : 'rest_of_week'
  const recipeIds = (Array.isArray(draft?.chosenRecipeIds) ? draft.chosenRecipeIds : [])
    .map((id) => String(id || '').trim())
    .filter(Boolean)
    .slice(0, 6)
  return {
    scope,
    days: scope === 'pick_nights' ? [...(draft.days || [])] : [],
    cook_nights: clampCookNights(draft?.cookNights),
    easy_night_types: easy,
    eat_out_day: draft?.eatOutDay === 'fri' || draft?.eatOutDay === 'sat' ? draft.eatOutDay : null,
    bias: draft?.bias === 'familiar' || draft?.bias === 'new' ? draft.bias : 'mix',
    replace_existing: Boolean(draft?.replaceExisting),
    ...(recipeIds.length ? { recipe_ids: recipeIds } : {}),
  }
}
